import React, { useContext, useCallback, useEffect, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import produce from 'immer';
import { Monitor, MonitorTargetType, MonitorAction, RobotDefinition } from '@moderepo/mode-apis';
import { capitalizeAll, capitalizeFirst, ITEMS_PER_PAGE_10, ITEMS_PER_PAGE_LIST, GenericMessageBox, PaginationBar, PAGING_MIN_PAGES,
    SelectInputOption, useSearchParams } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchMonitors, fetchRobotCatalog, rcDataContext, selectMonitors, selectRobotCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCMonitorsTable } from '../../components/robot-cloud';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { RCMonitorTableFilters, RCMonitorsTableDataItem, RCMonitorsTableProps }
    from '../../components/robot-cloud/RCMonitorsTable';
import * as RCUtils from './RCUtils';



/**
 * Create a list of robot Class options to be used for the filter by robot Class dropdown
 * @param trans
 * @param robotCatalog
 */
const createFilterByRobotClassOptions = (trans: TFunction, robotCatalog: readonly RobotDefinition[] | undefined): SelectInputOption<string>[] => {
    if (robotCatalog) {
        return [{
            label: capitalizeAll(trans('label.show_all')),
            value: '',
        }, ...(robotCatalog.map((robotDef: RobotDefinition): SelectInputOption<string> => {
            return {
                label: robotDef.robotClass,
                value: robotDef.robotClass,
            };
        }))];
    }

    return [];
};


/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'true'
 */
export interface MonitorsTableHiddenFields {
    readonly id?: boolean;
    readonly status?: boolean;
    readonly name?: boolean;
    readonly severity?: boolean;
    readonly target?: boolean;
    readonly targetHomeId?: boolean;
    readonly conditions?: boolean;
    readonly actions?: boolean;
    readonly updatedAgentId?: boolean;
    readonly updatedAt?: boolean;
    readonly createdAgentId?: boolean;
    readonly createdAt?: boolean;
}



interface RCMonitorsContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly itemsPerPage?: number;
    readonly selectedMonitorId: number | undefined;
    readonly hiddenFields?: MonitorsTableHiddenFields;
    readonly canRemoveAlerts: boolean;
    readonly canCreateAlerts: boolean;
    readonly onAlertSelected: (monitor: Monitor)=> void;
    readonly onCreateAlertClicked?: ()=> void;
    readonly onRemoveAlertClicked?: (monitor: Monitor)=> void;
}


interface MonitorSearchFields {
    readonly robotClass: string | undefined
    readonly robotId: number | undefined
}



/**
 * This container display ALL the Project's Monitors. We created a container for this instead of placing this code in the MonitorsPage so that
 * this can be reused and shown in other pages.
 */
export const RCMonitorsContainer = (props: RCMonitorsContainerProps) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const robotCatalog = selectRobotCatalog(rcDataState, props.projectId);


    // Setup the Search and pagination options
    const {
        searchParams, onItemsPerPageChange, onOtherFieldsChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<MonitorSearchFields>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            robotClass: undefined,
            robotId   : undefined,
        },
    });


    // Select the list of Monitors from DataState based on the current filters
    const monitors = selectMonitors(rcDataState, props.projectId, {
        pageNumber: searchParams.currentPage,
        pageSize  : searchParams.itemsPerPage,
        robotClass: searchParams.otherFields.robotClass,
        robotId   : searchParams.otherFields.robotId,
    });



    // Filtered by robot class options. We will call createFilterByRobotClassOptions to create the list of options based on the list of
    // robot classes.
    const robotClassFilterOptions = useMemo(() => {
        return createFilterByRobotClassOptions(trans, robotCatalog);
    }, [trans, robotCatalog]);



    /**
     * This effect is used for loading Robot catalog if we don't have it yet
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(props.projectId));
        }
    }, [dataDispatch, robotCatalog, props.projectId, rcDataDispatch]);


    /**
     * Fetch monitors base on the current pagination params
     */
    const fetchMoreMonitors = useCallback(async () => {
        if (project) {
            rcDataDispatch(
                fetchMonitors(project.id, searchParams.currentPage, searchParams.itemsPerPage, {
                    robotClass: searchParams.otherFields.robotClass,
                    robotId   : searchParams.otherFields.robotId,
                }),
            ).catch(() => {
                // ignore error because dataDispatch already took care of the error
            });
        }
    }, [project, rcDataDispatch, searchParams.currentPage, searchParams.itemsPerPage, searchParams.otherFields.robotClass,
        searchParams.otherFields.robotId]);



    /**
     * This use effect is used for fetching more Monitors when the itemsPerPage or currentPage from the table pagination changed.
     */
    useEffect(() => {
        fetchMoreMonitors();
    }, [dataDispatch, fetchMoreMonitors]);



    /**
     * This used effect is used for loading Monitors on component loads or when Monitor are cleared
     */
    useEffect(() => {
        if (monitors === undefined) {
            fetchMoreMonitors();
        }
    }, [dataDispatch, monitors, fetchMoreMonitors]);



    // Handlers for robot class filter changed event
    const onFilterByRobotClassChange = useCallback((robotClass: string | '') => {
        if (robotClass) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {
                draft.robotClass = robotClass;
            }));
        } else {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {
                draft.robotClass = undefined;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);



    // Create the filter settings to be passed to the MonitorsTableComp
    const monitorsFilterSettings: RCMonitorTableFilters = useMemo(() => {
        return {
            robotClass: {
                currentValue: searchParams.otherFields.robotClass,
                label       : capitalizeAll(trans('label.monitor_filter.robot_class')),
                options     : robotClassFilterOptions,
                onChange    : onFilterByRobotClassChange,
            },
        };
    }, [trans, searchParams.otherFields, robotClassFilterOptions, onFilterByRobotClassChange]);



    /**
     * Localize the Monitors data before passing it to the MonitorsTableComp for display
     */
    const localizeMonitorsData = useMemo((): readonly RCMonitorsTableDataItem[] | undefined => {
        const monitorList: readonly Monitor[] | undefined = monitors?.items;

        if (monitorList && monitorList.length > 0) {
            return monitorList.map((monitor: Monitor): RCMonitorsTableDataItem => {
                const target = monitor.targetType === MonitorTargetType.ROBOT_ID
                    ? capitalizeAll(trans('label.robot_and_robot_id', { robot_id: monitor.targetRobotId }))
                    : capitalizeFirst(trans('label.robot_class_and_class_name', { class_name: monitor.targetRobotClass }));

                return {
                    actualValue : monitor,
                    displayValue: {
                        id      : monitor.id.toString(),
                        status  : monitor.disabled ? trans('label.disabled') : trans('label.enabled'),
                        name    : monitor.name,
                        severity: monitor.severity
                            ? capitalizeAll(trans(RCUtils.getMonitorSeverityName(monitor.severity))) : capitalizeFirst(trans('label.none')),
                        target,
                        targetHomeId: monitor.targetHomeId ? monitor.targetHomeId.toString() : '',
                        condition   : monitor.condition.toString(),
                        actions     : monitor.actions.map((action: MonitorAction) => {
                            return RCUtils.getMonitorActionTypeName(action.type);
                        }).join(', '),
                        updatedAgentId: monitor.updatedAgentId.toString(),
                        updatedAt     : getDateTimeAndElapsedTime(monitor.updatedAt, trans, false),
                        createdAgentId: monitor.createdAgentId.toString(),
                        createdAt     : getDateTimeAndElapsedTime(monitor.createdAt, trans, false),
                    },
                    canBeRemoved: true,
                    selected    : props.selectedMonitorId === monitor.id,
                };
            });
        }

        return undefined;
    }, [trans, props.selectedMonitorId, monitors]);


    // The table pagination settings that we need to pass to the MonitorsTableComp. We will create and cache it instead of creating it each time we
    // re-render this container.
    const monitorsTablePaginationBar = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : monitors && monitors.range ? monitors.range.total : undefined,
                hasMoreItems    : (monitors && monitors.range
                    ? monitors.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (!monitors || monitors.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('label.item', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, monitors, searchParams, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);


    // Create the table settings for the MonitorsTable
    const monitorsTableSettings = useMemo((): RCMonitorsTableProps['fieldsSettings'] => {
        return {
            fields: {
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.id,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.monitor_name')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.name,
                },
                target: {
                    dataItemProp: 'target',
                    label       : capitalizeAll(trans('label.monitor_target')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.target,
                },
                targetHomeId: {
                    dataItemProp: 'targetHomeId',
                    label       : capitalizeAll(trans('label.target_home_id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.targetHomeId,
                },
                severity: {
                    dataItemProp: 'severity',
                    label       : capitalizeAll(trans('label.severity')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.severity,
                },
                condition: {
                    dataItemProp: 'conditions',
                    label       : capitalizeAll(trans('label.condition', { count: 2 })),
                    sortable    : false,
                    hidden      : props.hiddenFields?.conditions,
                },
                actions: {
                    dataItemProp: 'actions',
                    label       : capitalizeAll(trans('label.monitor_action', { count: 2 })),
                    sortable    : false,
                    hidden      : props.hiddenFields?.actions,
                },
                updatedAgentId: {
                    dataItemProp: 'updatedAgentId',
                    label       : capitalizeAll(trans('label.updated_by')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.updatedAgentId,
                },
                updatedAt: {
                    dataItemProp: 'updatedAt',
                    label       : capitalizeAll(trans('label.date_updated')),
                    hidden      : props.hiddenFields?.updatedAt,
                },
                createdAgentId: {
                    dataItemProp: 'agentId',
                    label       : capitalizeAll(trans('label.agent_id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.createdAgentId,
                },
                createdAt: {
                    dataItemProp: 'createdAt',
                    label       : capitalizeAll(trans('label.date')),
                    hidden      : props.hiddenFields?.createdAt,
                },
                status: {
                    dataItemProp: 'status',
                    label       : capitalizeAll(trans('label.status')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.status,
                },
            },
        };
    }, [trans, props.hiddenFields]);


    return (
        <>
            {project && monitors && (
                <RCMonitorsTable
                    className={clsx(props.className)}
                    listData={localizeMonitorsData}
                    title={capitalizeAll(trans('label.monitor', { count: 2 }))}
                    icon="visibility"
                    filters={monitorsFilterSettings}
                    customPrimaryActions={props.canCreateAlerts && props.onCreateAlertClicked ? [{
                        label  : capitalizeAll(trans('label.add')),
                        icon   : 'add',
                        variant: 'contained',
                        color  : 'primary',
                        onClick: props.onCreateAlertClicked,
                    }] : []}
                    onListItemSelected={props.onAlertSelected}
                    onRemoveItemClicked={props.onRemoveAlertClicked}
                    paginationComp={monitorsTablePaginationBar}
                    fieldsSettings={monitorsTableSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title',
                                { term: capitalizeAll(trans('label.monitor', { count: 2 })) })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.monitor', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}


            {!project && (
                <CircularProgress />
            )}

        </>
    );
};
